import { AdditiveBlending, Color, Object3D, ShaderMaterial, Texture, Vector3 } from 'three'
import { GlobalParticlesOptions } from './global-particles-manager'
import GPUParticleContainer from './gpu-particles-container'
import particlesVS from './particles-vs.glsl'
import particlesFS from './particles-fs.glsl'

export interface Size {
  width: number
  height: number
}

export interface SpawnParticleOptions {
  position: Vector3
  velocity?: Vector3
  color?: Color
  positionRandomness?: number
  velocityRandomness?: number
  sizeRandomness?: number
  colorRandomness?: number
  turbulence?: number
  lifetime?: number
  size?: number
  smoothPosition?: boolean
  opacity?: number
  startTime?: number
  trailID?: number
}

export default class GlobalParticlesSystem extends Object3D {
  options: GlobalParticlesOptions

  PARTICLE_COUNT = 1000000
  PARTICLE_CONTAINERS = 1

  PARTICLE_NOISE_TEXTURE: Texture | undefined
  PARTICLE_SPRITE_TEXTURE: Texture | undefined

  PARTICLES_PER_CONTAINER: number
  PARTICLE_CURSOR = 0
  time = 0
  gravity = 0
  particleContainers: GPUParticleContainer[] = []
  particleShaderMat: ShaderMaterial

  constructor(options: GlobalParticlesOptions) {
    super()

    this.options = Object.assign({}, options)

    this.PARTICLE_COUNT = options.maxParticles || 1000000
    this.PARTICLE_CONTAINERS = options.containerCount || 1

    if (options.particleNoiseTex) {
      this.PARTICLE_NOISE_TEXTURE = options.particleNoiseTex
    }

    if (options.particleSpriteTex) {
      this.PARTICLE_SPRITE_TEXTURE = options.particleSpriteTex
    }

    this.PARTICLES_PER_CONTAINER = Math.ceil(this.PARTICLE_COUNT / this.PARTICLE_CONTAINERS)
    this.PARTICLE_CURSOR = 0
    this.time = 0
    this.gravity = 0
    this.particleContainers = []

    this.particleShaderMat = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      uniforms: {
        uTime: {
          value: 0.0
        },
        uScale: {
          value: 1.0
        },
        opacity: {
          value: 1.0
        },
        tNoise: {
          value: this.PARTICLE_NOISE_TEXTURE
        }
      },
      blending: AdditiveBlending,
      vertexShader: particlesVS,
      fragmentShader: particlesFS
    })

    this.particleShaderMat.defaultAttributeValues.particlePositionsStartTime = [0, 0, 0, 0]
    this.particleShaderMat.defaultAttributeValues.particleVelColSizeLife = [0, 0, 0, 0]

    this.init()
  }

  init() {
    for (let i = 0; i < this.PARTICLE_CONTAINERS; i++) {
      const c = new GPUParticleContainer(this.PARTICLES_PER_CONTAINER, this)
      this.particleContainers.push(c)
      this.add(c)
    }
  }

  spawnParticle(options: SpawnParticleOptions) {
    this.PARTICLE_CURSOR++

    if (this.PARTICLE_CURSOR >= this.PARTICLE_COUNT) {
      this.PARTICLE_CURSOR = 1
    }

    const currentContainer = this.particleContainers[Math.floor(this.PARTICLE_CURSOR / this.PARTICLES_PER_CONTAINER)]

    currentContainer.spawnParticle(options)
  }

  /*applyAttractingForce(position: Vector3) {
    for (let i = 0; i < this.PARTICLE_CONTAINERS; i++) {
      this.particleContainers[i].applyAttractingForce(position)
    }
  }*/

  update(time: number) {
    for (let i = 0; i < this.PARTICLE_CONTAINERS; i++) {
      this.particleContainers[i].update(time)
      //this.particleContainers[i].updateVelocity(time)
    }
  }

  resize(size: Size) {
    for (let i = 0; i < this.PARTICLE_CONTAINERS; i++) {
      this.particleContainers[i].resize(size)
    }
  }

  dispose() {
    this.particleShaderMat.dispose()

    for (let i = 0; i < this.PARTICLE_CONTAINERS; i++) {
      this.particleContainers[i].dispose()
    }
  }
}
